
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function SmartLightingInstallationPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Smart Lighting Installation',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Professional smart lighting installation in Fresno, CA. Automated lighting control, voice commands, scheduling, and energy-efficient LED smart bulbs.',
    url: `${siteUrl}/services/smart-home-energy/smart-lighting-installation`,
  };

  return (
    <>
      <SEOHead
        title="Smart Lighting Installation Fresno CA | Automated Home Lighting"
        description="Smart lighting installation in Fresno, CA. Voice-controlled lights, automated scheduling, dimming, and energy-efficient LED smart bulbs. Professional electrician installation."
        canonical="/services/smart-home-energy/smart-lighting-installation"
        keywords="smart lighting Fresno, automated lighting installation, voice controlled lights, smart bulbs Fresno"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Smart Lighting Installation — Fresno, CA"
        heroSubtitle="Voice-controlled, automated lighting for modern Fresno homes"
        heroImage="https://public.readdy.ai/ai/img_res/ce825959dd856600d851a5abea8c54b9.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Smart%20light%20switch%20panel%20with%20LED%20indicators%20and%20dimmer%20controls%20mounted%20on%20modern%20wall%20with%20smartphone%20showing%20lighting%20control%20app%20interface%20glowing%20smart%20bulbs%20visible%20in%20background%20contemporary%20interior%20no%20people&width=700&height=600&seq=smartlight-fresno-01&orientation=portrait"
        fresnoContent="Fresno homeowners are transforming their lighting with smart technology that saves energy and adds convenience. Our smart lighting installations are perfect for Fresno's long summer days and warm evenings, allowing you to automate outdoor lighting, create custom scenes, and control every light with your voice or smartphone. We serve all Fresno neighborhoods including Woodward Park, Fig Garden, Tower District, and Clovis with professional smart lighting installations that integrate seamlessly with Alexa, Google Home, and Apple HomeKit."
        features={[
          {
            icon: 'ri-lightbulb-flash-line',
            title: 'Smart Bulb Installation',
            description: 'Color-changing LED bulbs with app control',
          },
          {
            icon: 'ri-toggle-line',
            title: 'Smart Switch Replacement',
            description: 'Replace traditional switches with smart controls',
          },
          {
            icon: 'ri-voice-recognition-line',
            title: 'Voice Control Setup',
            description: 'Alexa, Google Assistant, and Siri integration',
          },
          {
            icon: 'ri-calendar-line',
            title: 'Automated Scheduling',
            description: 'Lights turn on/off based on your routine',
          },
          {
            icon: 'ri-contrast-2-line',
            title: 'Dimming Control',
            description: 'Adjust brightness from anywhere',
          },
          {
            icon: 'ri-sun-line',
            title: 'Daylight Sensors',
            description: 'Automatic adjustment based on natural light',
          },
        ]}
        whatsIncluded={[
          'Complete smart lighting consultation and design',
          'Professional installation of smart switches and bulbs',
          'Voice assistant integration (Alexa, Google, Siri)',
          'Mobile app setup and configuration',
          'Automated scheduling and scene creation',
          'Dimming and color control setup',
          'Energy monitoring and usage reports',
          'Integration with existing smart home systems',
          'Complete system testing and demonstration',
          'Ongoing support and troubleshooting',
        ]}
        faqs={[
          {
            question: 'How much energy can I save with smart lighting in Fresno?',
            answer:
              "Fresno homeowners typically save 60-80% on lighting costs with smart LED bulbs compared to traditional incandescent bulbs. Smart lighting also reduces energy waste through automated scheduling—lights turn off when you forget, and outdoor lights adjust based on sunset times. With Fresno's long summer days, automated scheduling ensures you're not running lights unnecessarily, and dimming capabilities further reduce consumption during evening hours.",
          },
          {
            question: 'Do smart lights work during power outages in Fresno?',
            answer:
              "Smart lights require power to function, so they won’t work during outages. However, once power is restored, they automatically reconnect to your Wi‑Fi and resume their programmed schedules. For critical lighting during Fresno power outages (which can occur during summer heat waves), we can integrate battery backup systems or recommend smart lights that retain their last settings when power returns.",
          },
          {
            question: 'Can you install smart lighting in my older Fresno home?',
            answer:
              "Yes! Most Fresno homes can be upgraded with smart lighting. For smart bulbs, you just need standard light sockets—no electrical work required. For smart switches, we verify your switch boxes have neutral wires (required for most smart switches). Older Fresno homes built before the 1980s may need minor wiring updates, which we handle as licensed electricians. All work meets California electrical codes.",
          },
          {
            question: 'What smart lighting brands do you install in Fresno?',
            answer:
              "We install all major smart lighting brands including Philips Hue, LIFX, Lutron Caseta, Leviton, TP‑Link Kasa, and GE Cync. We help you choose the best system based on your budget, desired features, and existing smart home ecosystem. For Fresno homes, we often recommend systems with strong outdoor ratings for patio and landscape lighting that can handle our hot, dry summers.",
          },
          {
            question: 'How long does smart lighting installation take in Fresno homes?',
            answer:
              "Installing smart bulbs throughout a typical Fresno home takes 1‑2 hours. Replacing 10‑15 traditional switches with smart switches takes 3‑4 hours. A comprehensive whole‑home smart lighting installation with outdoor landscape lighting, automated scenes, and voice control integration typically takes 1 full day. We work efficiently and clean up completely, leaving your home ready to enjoy your new smart lighting system.",
          },
          {
            question: 'Can smart lighting integrate with my security system in Fresno?',
            answer:
              "Absolutely! Smart lighting is a key component of home security in Fresno. We can program your lights to turn on when motion is detected, simulate occupancy when you're away, and integrate with security cameras and alarm systems. For Fresno homes, we often set up automated outdoor lighting that activates at dusk and responds to motion sensors, deterring potential intruders while saving energy.",
          },
        ]}
        ctaText="Get Your Free Smart Lighting Quote"
      />
    </>
  );
}
